/**
 * Empty-search recommendations for DEV-234005. Static until a content source replaces them.
 *
 * Kameleoon feature `trending-searches`:
 * - `data` — variant 1, ranked from our search data
 * - `marketing` — variant 2, the marketing list
 *
 * Any other value, including a missing flag, keeps today's search bar. The test is DACH only
 * (`de-DE`, `de-AT`, `de-CH`). Locally, one request header is enough:
 * `x-kml-variations: trending-searches:data` or `trending-searches:marketing`.
 */

export type TrendingSearchesArm = 'data' | 'marketing';

export type TrendingLicense = {
  name: string;
  slug: string;
  userId: string;
  /** Dropped on de-AT and de-CH. The acceptance criteria name these two shops. */
  deOnly?: boolean;
};

export type TrendingTerm = {
  /** Display only. The link is built from `categoryId` and `searchTerm`. */
  name: string;
  /** Marketplace category the search runs in. `null` searches the whole shop. */
  categoryId: number | null;
  searchTerm: string;
};

/** Marketplace category ID → the slug chain its PLP URL needs. */
export type TrendingCategorySlugs = Record<number, string[]>;

const DACH_LOCALES = new Set(['de-DE', 'de-AT', 'de-CH']);

export const isTrendingSearchesLocale = (locale: string) => DACH_LOCALES.has(locale);

/** Desktop shows every term. Below the header's 834px split, the panel hides the rest. */
export const MOBILE_TRENDING_SEARCH_COUNT = 4;

const DATA_TERMS: TrendingTerm[] = [
  { name: 'Weihnachten', categoryId: null, searchTerm: 'weihnachten' },
  { name: 'Katze', categoryId: null, searchTerm: 'katze' },
  { name: 'Dackel', categoryId: null, searchTerm: 'dackel' },
  { name: 'Fahrrad', categoryId: null, searchTerm: 'fahrrad' },
  { name: 'Rente', categoryId: null, searchTerm: 'rente' },
  { name: 'Feuerwehr', categoryId: null, searchTerm: 'feuerwehr' },
];

const DATA_LICENSES: TrendingLicense[] = [
  { name: 'Peanuts Official', slug: 'peanuts+official', userId: '124991195' },
  { name: 'Sesamstrasse', slug: 'sesamstrasse', userId: '118437010' },
  { name: 'Pumuckl Official', slug: 'pumuckl+official', userId: '213100150' },
  { name: 'Pepsico De', slug: 'pepsico+de', userId: '214603125', deOnly: true },
  { name: 'Harry Potter', slug: 'harry+potter', userId: '116443435' },
  { name: 'Die Drei Fragezeichen', slug: 'die+drei+fragezeichen', userId: '118714651' },
  { name: 'Star Wars Classics', slug: 'star+wars+classics', userId: '213821169' },
  { name: 'Gilmore Girls', slug: 'gilmore+girls', userId: '213100339' },
  { name: 'Weihnachtsmann Und Co Kg', slug: 'weihnachtsmann+und+co+kg', userId: '213694120' },
  { name: 'Janosch', slug: 'janosch', userId: '8443384' },
];

const MARKETING_TERMS: TrendingTerm[] = [
  { name: 'Ugly Christmas Sweater', categoryId: 1002, searchTerm: 'ugly christmas' },
  { name: 'Weihnachten', categoryId: null, searchTerm: 'weihnachten' },
  { name: 'Lustige T-Shirts', categoryId: 1001, searchTerm: 'lustige' },
  { name: 'Geschenke für Mama', categoryId: null, searchTerm: 'mama' },
  { name: 'Geschenke für Papa', categoryId: null, searchTerm: 'papa' },
  {
    name: 'Geschenke für beste Freunde',
    categoryId: null,
    searchTerm: 'beste freunde',
  },
];

const MARKETING_LICENSES: TrendingLicense[] = [
  { name: 'Peanuts Official', slug: 'peanuts+official', userId: '124991195' },
  { name: 'Die Drei Fragezeichen', slug: 'die+drei+fragezeichen', userId: '118714651' },
  { name: 'Harry Potter', slug: 'harry+potter', userId: '116443435' },
  { name: 'Bibi & Tina', slug: 'bibiundtina', userId: '6277160', deOnly: true },
  { name: 'Sesamstrasse', slug: 'sesamstrasse', userId: '118437010' },
  { name: 'The Grinch', slug: 'the+grinch', userId: '213694256' },
  { name: 'Friends Tv Series', slug: 'friends+tv+series', userId: '117920759' },
  { name: 'Star Trek', slug: 'star+trek', userId: '125925309' },
  { name: 'Asterix', slug: 'asterix-eu', userId: '4043504' },
  { name: 'Janosch', slug: 'janosch', userId: '8443384' },
];

export const resolveTrendingSearchesArm = (
  variant: string | undefined,
  locale: string
): TrendingSearchesArm | null => {
  if (!isTrendingSearchesLocale(locale)) return null;
  if (variant === 'data' || variant === 'marketing') return variant;
  return null;
};

export const getTrendingSearches = (arm: TrendingSearchesArm, locale: string) => {
  const terms = arm === 'marketing' ? MARKETING_TERMS : DATA_TERMS;
  const licenses = (arm === 'marketing' ? MARKETING_LICENSES : DATA_LICENSES).filter(
    (license) => locale === 'de-DE' || !license.deOnly
  );

  return { terms, licenses };
};

/** Both arms' category IDs: the arm is picked on the client, after the slugs are resolved. */
export const TRENDING_CATEGORY_IDS = [
  ...new Set(
    [...DATA_TERMS, ...MARKETING_TERMS].flatMap(({ categoryId }) =>
      categoryId === null ? [] : [categoryId]
    )
  ),
];
